import { useState, useCallback, useRef } from 'react';

export interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  followUpQuestions?: string[];
  interactiveElement?: {
    type: string;
    content: string;
    metadata?: Record<string, any>;
  };
  isStreaming?: boolean;
  enhancementPending?: boolean;
}

interface InteractiveElement {
  type: string;
  content: string;
  metadata?: Record<string, any>;
}

interface StreamingChatOptions {
  initialMessages?: ChatMessage[];
  projectContext?: string | null;
}

interface UseStreamingChatReturn {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sendMessage: (content: string) => Promise<void>;
  isStreaming: boolean;
  error: string | null;
  projectContext: string | null;
  setProjectContext: (context: string | null) => void;
}

export function useStreamingChat(options: StreamingChatOptions = {}): UseStreamingChatReturn {
  const { initialMessages = [] } = options;

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectContext, setProjectContext] = useState<string | null>(options.projectContext || null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isStreaming) return;

    setError(null);
    setIsStreaming(true);

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: content.trim(),
      role: 'user',
      timestamp: new Date()
    };

    // Create placeholder assistant message
    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      isStreaming: true,
      enhancementPending: true,
      followUpQuestions: [],
      interactiveElement: undefined
    };

    // Send the full conversation history (not just the new message) so the
    // assistant has context for follow-up questions.
    const conversationHistory = [...messages, userMessage]
      .filter(msg => msg.content.trim())
      .map(msg => ({ role: msg.role, content: msg.content }))
      .slice(-20); // keep payload/context bounded for long conversations

    setMessages(prev => [...prev, userMessage, assistantMessage]);

    try {
      const response = await fetch('/api/chat-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: conversationHistory,
          projectContext
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const dataStr = line.slice(6); // Remove 'data: ' prefix
          if (dataStr === '[DONE]') continue;

          try {
            const data = JSON.parse(dataStr);

            if (data.type === 'text') {
              // Append text delta to message
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: msg.content + data.delta }
                  : msg
              ));
            } else if (data.type === 'text-done') {
              // Main text finished — stop the "typing" cursor right away
              // instead of holding it through the follow-up generation step.
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, isStreaming: false }
                  : msg
              ));
            } else if (data.type === 'done') {
              // Add structured data (follow-ups and visualization)
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      isStreaming: false,
                      enhancementPending: false,
                      followUpQuestions: data.followUpQuestions || [],
                      interactiveElement: data.interactiveElement as InteractiveElement
                    }
                  : msg
              ));
            } else if (data.type === 'error') {
              const errorMessage = data.message || 'An error occurred';
              setError(errorMessage);
              // Surface the friendly message in the chat bubble itself —
              // otherwise the assistant message stays blank (no 'text'
              // events were ever sent for this turn).
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId && !msg.content
                  ? { ...msg, content: errorMessage }
                  : msg
              ));
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError, dataStr);
          }
        }
      }

      // Ensure streaming/enhancement state is cleared even if the connection
      // closed without an explicit 'done' event (e.g. the function timed
      // out mid-Phase-2, after 'text-done' already cleared isStreaming).
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId && (msg.isStreaming || msg.enhancementPending)
          ? { ...msg, isStreaming: false, enhancementPending: false }
          : msg
      ));

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was cancelled, don't show error
        return;
      }

      console.error('Streaming chat error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');

      // Update message with error fallback
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? {
              ...msg,
              content: msg.content || "I'm having trouble connecting right now, but you can always reach out to Yaniv directly!",
              isStreaming: false,
              enhancementPending: false,
              followUpQuestions: ["How can I contact Yaniv?"],
              interactiveElement: {
                type: 'contact',
                content: 'Ways to contact Yaniv',
                metadata: {
                  contacts: [
                    { id: 'email', title: 'Email', subtitle: 'Drop me a line', value: 'yanivvds@gmail.com', link: 'mailto:yanivvds@gmail.com' },
                    { id: 'linkedin', title: 'LinkedIn', subtitle: "Let's connect", value: '/in/yanivvds', link: 'https://www.linkedin.com/in/yanivvds/' }
                  ]
                }
              }
            }
          : msg
      ));
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [isStreaming, projectContext, messages]);

  return {
    messages,
    setMessages,
    sendMessage,
    isStreaming,
    error,
    projectContext,
    setProjectContext
  };
}

export default useStreamingChat;
